import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";

import { addEdges, GraphEdge } from "../../../api";
import { updateEdges } from "../../../api/sdk.gen.ts";
import { TIMING_CONFIG } from "../../../constants/timing";
import { $syncedGraphContent } from "../../../stores/graph/content.ts";
// Edge creation configuration
const EDGE_CONFIG = {
  MAX_CONCURRENT_EDGES: 2,
  SEMAPHORE_WAIT_MS: TIMING_CONFIG.EDGE_SEMAPHORE_WAIT_MS,
} as const;

let edgeCreationSemaphore = 0;

export const edgeOperations = {
  async create({
    graphId,
    sourceId,
    targetId,
  }: {
    graphId: null | string;
    sourceId: string;
    targetId: string;
  }): Promise<void> {
    const content = $syncedGraphContent.get();
    if (!content || !graphId) return;

    while (edgeCreationSemaphore >= EDGE_CONFIG.MAX_CONCURRENT_EDGES) {
      await new Promise((resolve) =>
        setTimeout(resolve, EDGE_CONFIG.SEMAPHORE_WAIT_MS),
      );
    }

    edgeCreationSemaphore++;

    try {
      const newEdge = {
        id: uuidv4(),
        sourceId,
        targetId,
        value: "Connects to",
      };

      $syncedGraphContent.set({
        ...content,
        edges: [...content.edges, newEdge],
      });
      await addEdges({ body: [newEdge], path: { id: graphId } });
    } finally {
      edgeCreationSemaphore--;
    }
  },

  /**
   * Creates edges from multiple source nodes to a single target node
   * Direction: Sources → Target
   */
  async createManyToOne({
    graphId,
    sourceIds,
    targetId,
    value = "Connects to",
  }: {
    graphId: string;
    sourceIds: string[];
    targetId: string;
    value?: string;
  }): Promise<void> {
    const content = $syncedGraphContent.get();
    if (!content || !graphId || sourceIds.length === 0) return;

    const newEdges: GraphEdge[] = sourceIds.map((sourceId) => ({
      id: uuidv4(),
      sourceId,
      targetId,
      value,
    }));

    $syncedGraphContent.set({
      ...content,
      edges: [...content.edges, ...newEdges],
    });

    await addEdges({ body: newEdges, path: { id: graphId } });

    toast.success(
      `Connected ${sourceIds.length} node${sourceIds.length > 1 ? "s" : ""} to target`,
    );
  },

  /**
   * Creates edges from a single source node to multiple target nodes
   * Direction: Source → Targets
   */
  async createOneToMany({
    graphId,
    sourceId,
    targetIds,
    value = "Connects to",
  }: {
    graphId: string;
    sourceId: string;
    targetIds: string[];
    value?: string;
  }): Promise<void> {
    const content = $syncedGraphContent.get();
    if (!content || !graphId || targetIds.length === 0) return;

    const newEdges: GraphEdge[] = targetIds.map((targetId) => ({
      id: uuidv4(),
      sourceId,
      targetId,
      value,
    }));

    $syncedGraphContent.set({
      ...content,
      edges: [...content.edges, ...newEdges],
    });

    await addEdges({ body: newEdges, path: { id: graphId } });

    toast.success(
      `Connected source to ${targetIds.length} node${targetIds.length > 1 ? "s" : ""}`,
    );
  },

  update({
    edgeId,
    graphId,
    newValue,
  }: {
    edgeId: string;
    graphId: string;
    newValue: string;
  }): void {
    const content = $syncedGraphContent.get();
    if (!content) return;

    const edgeIndex = content.edges.findIndex((edge) => edge.id === edgeId);
    if (edgeIndex === -1) return;

    const updatedEdge: GraphEdge = {
      ...content.edges[edgeIndex],
      value: newValue,
    };

    const updatedEdges = [...content.edges];
    updatedEdges[edgeIndex] = updatedEdge;

    $syncedGraphContent.set({
      ...content,
      edges: updatedEdges,
    });

    void updateEdges({ body: [updatedEdge], path: { id: graphId } });
  },
};
